import { ComponentPortal, ComponentType, DomPortalOutlet } from '@angular/cdk/portal';
import { IFicScreenConfig } from './fic-screen';
import {
    ApplicationRef,
    ComponentFactoryResolver,
    Inject,
    Injectable,
    Injector,
    Renderer2,
    RendererFactory2,
} from '@angular/core';
import { FicScreenRefModel } from './fic-screen-ref';
import { FicScreenConfig, FicScreenRef } from './fic-screen-injecotors';
import { FicScreenPortalComponent } from './fic-screen-portal.component';
import { StateService } from '@uirouter/angular';
import { FicScreenContentBase } from './fic-screen-content-base';
import { takeUntil } from 'rxjs/operators';
import { SaveChangesWarningService } from '../fi-shared-services/save-changes-warning.service';

@Injectable()
export class FicScreenService {
    protected _renderer: Renderer2;

    constructor(
        protected _componentFactoryResolver: ComponentFactoryResolver,
        protected _appRef: ApplicationRef,
        protected _injector: Injector,
        @Inject(RendererFactory2) rendererFactory2: RendererFactory2,
        protected stateService: StateService,
        protected saveChangesWarningService: SaveChangesWarningService,
    ) {
        this._renderer = rendererFactory2.createRenderer(null, null);
    }

    /*
        use FicScreenHelperService for opening various screens
     */
    openScreen<TData, TRes>(
        config: IFicScreenConfig<TData, TRes>,
    ): FicScreenRefModel<FicScreenContentBase<TData, TRes>, TRes> {
        this.showScreenPortalElement();
        const screenOutletElement = this.getScreenPortalElement(),
            screenRef = new FicScreenRefModel<ComponentType<FicScreenContentBase<TData, TRes>>, TRes>(),
            injector = Injector.create({
                providers: [
                    {
                        provide: FicScreenConfig,
                        useValue: config,
                    },
                    {
                        provide: FicScreenRef,
                        useValue: screenRef,
                    },
                ],
                parent: this._injector,
            }),
            portalOutlet = new DomPortalOutlet(screenOutletElement, this._appRef, this._injector),
            componentPortal = new ComponentPortal(FicScreenPortalComponent, null, injector);
        screenRef.screenComponentRef = portalOutlet.attachComponentPortal(componentPortal);

        // https://chainformation.atlassian.net/browse/FI-3677
        let skipCheckChangesOnClose = false;
        screenRef.beforeClose
            .pipe(takeUntil(screenRef.screenComponentRef.instance.ngOnDestroy$))
            .subscribe(($event) => {
                if (!skipCheckChangesOnClose && this.saveChangesWarningService.hasChanges()) {
                    $event.preventDefault();
                    setTimeout(() => {
                        this.saveChangesWarningService.showConfirmDialog().onYesButtonClick(() => {
                            // don't show 'leave page' dialog again if user clicks 'leave page'
                            skipCheckChangesOnClose = true;
                            screenRef.close();
                            skipCheckChangesOnClose = false;
                            this.saveChangesWarningService.stop();
                        });
                    }, 10);
                }
            });
        this._attachStateToScreenRef<FicScreenContentBase<TData, TRes>, TRes>(screenRef);
        return screenRef;
    }

    protected _createScreenPortalElement() {
        const screenPortalElement = document.createElement('DIV');
        this._renderer.addClass(screenPortalElement, 'fic-screen-portal');
        document.body.appendChild(screenPortalElement);
        return screenPortalElement;
    }

    _screenPortalElement: HTMLElement;

    getScreenPortalElement() {
        return this._screenPortalElement || (this._screenPortalElement = this._createScreenPortalElement());
    }

    hideScreenPortalElement() {
        this._renderer.addClass(this.getScreenPortalElement(), 'hidden');
    }
    showScreenPortalElement() {
        this._renderer.removeClass(this.getScreenPortalElement(), 'hidden');
    }

    protected _attachStateToScreenRef<TComp, TRes>(screenRef: FicScreenRefModel<TComp, TRes>) {
        ((onpopstate) => {
            let onClose = screenRef.onClose.subscribe(() => {
                window.onpopstate = () => (window.onpopstate = onpopstate);
                window.history.back();
            });

            window.onpopstate = processPopState;

            function processPopState() {
                // try to close
                if (screenRef.emitBeforeClose()) {
                    // add new onpopstate callback to set window.onpopstate to current function because
                    // window.history.go(1) will call window.onpopstate again
                    window.onpopstate = () => (window.onpopstate = processPopState);
                    window.history.forward();
                } else {
                    if (onClose) {
                        onClose.unsubscribe();
                        onClose = undefined;
                    }
                    screenRef.processClose();
                    window.onpopstate = onpopstate;
                }
            }
        })(window.onpopstate);

        const href = this.stateService.href('protected.screen');
        window.history.pushState({}, document.title, href);
    }
}
